import { useEffect, useState } from 'react';
import { Link } from 'foundations-components/transitional/components/Link';
import { Alert } from 'foundations-components/transitional/components/Alert';
import Pre from './Pre';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import devLogger from 'react-utils/devLogger';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Matches domain, org and repo name, and file path excluding `blob/`.
 *
 * @example
 * ['https://github.com/HubSpot/ui-extensions-examples/blob/main/flex-and-box/src/app/extensions/components/PropertyForm.tsx',
 * 'https://github.com/',
 * 'HubSpot/ui-extensions-examples'
 * 'main/flex-and-box/src/app/extensions/components/PropertyForm.tsx']
 *
 * @see https://regexr.com/8c3ti
 */
const GitHubUrlRegex = new RegExp(/(^https?:\/\/github.com\/)([a-zA-Z\d-]+\/[a-zA-Z\d-]+)(?:\/blob\/)(.+$)/);
const getFilePaths = (liveUrl, permalinkUrl) => {
  const [, domain, orgAndRepo, livePath] = GitHubUrlRegex.exec(liveUrl) || [];
  const [,,, permalinkPath] = GitHubUrlRegex.exec(permalinkUrl) || [];
  return {
    repoUrl: domain && orgAndRepo ? `${domain}${orgAndRepo}` : 'https://github.com/HubSpot',
    livePath: livePath ? `${orgAndRepo}/${livePath}` : null,
    permalinkPath: permalinkPath ? `${orgAndRepo}/${permalinkPath}` : null
  };
};

/**
 * Given a file path that contains user/organization, repository, and full file
 * path, returns its `raw.githubusercontent.com` URL.
 */
const getRawSrcUrlFromPath = path => {
  if (!path) return null;
  return `https://raw.githubusercontent.com/${path}`;
};
const GitHubEmbed = ({
  lang = 'js',
  url,
  permalinkUrl
}) => {
  const [error, setError] = useState(false);
  const {
    livePath,
    permalinkPath,
    repoUrl
  } = getFilePaths(url, permalinkUrl);
  const [src, setSrc] = useState();
  const liveSrcUrl = getRawSrcUrlFromPath(livePath);
  const permalinkSrcUrl = getRawSrcUrlFromPath(permalinkPath);

  /**
   * Docs URL shown locally to tech writers and other editors.
   */
  const warningDocsUrl =
  // eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils
  'https://local.hubteamqa.com/developer-docs-mdx-components/storybook/?path=/docs/editor-components-githubembed--page';

  /** Local warning with the prop values that were passed in for debugging. */
  const warningUrlReference = `You provided:\n  1. url="${url}"\n  2. permalinkUrl="${permalinkUrl}"`;
  useEffect(() => {
    if (!liveSrcUrl || !permalinkSrcUrl) {
      devLogger.warn({
        key: 'githubembed-missing',
        message: `GitHubEmbed: Please provide both live and permalink fallback URLs. ${warningUrlReference}`,
        url: warningDocsUrl
      });
      setError(true);
      return;
    }
    fetch(liveSrcUrl).then(liveRes => {
      // Current file
      if (liveRes.status === 200) {
        return liveRes.text();
      }
      devLogger.warn({
        key: 'githubembed-live-invalid',
        message: `GitHubEmbed: Current URL invalid. Please verify that the provided current URL is valid. ${warningUrlReference}`,
        url: warningDocsUrl
      });

      // Fallback
      return fetch(permalinkSrcUrl).then(fallbackRes => {
        if (fallbackRes.status === 200) {
          return fallbackRes.text();
        }
      }).catch(e => {
        throw e;
      });
    }).then(txt => {
      if (!txt) {
        throw new Error('Unable to fetch GitHub source from provided URLs');
      }
      setSrc(txt);
    }).catch(() => {
      devLogger.warn({
        key: 'githubembed-both-invalid',
        message: `GitHubEmbed: Fallback URL invalid. Please verify that the provided permalink fallback URL is valid. ${warningUrlReference}`,
        url: warningDocsUrl
      });
      setError(true);
    });
  }, [liveSrcUrl, permalinkSrcUrl, warningDocsUrl, warningUrlReference]);
  if (error) {
    return /*#__PURE__*/_jsxs(Alert, {
      type: "warning",
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "developer-docs-mdx-components.codeBlocks.githubError_jsx",
          elements: {
            Link
          },
          options: {
            href: repoUrl,
            external: true
          }
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "developer-docs-mdx-components.codeBlocks.githubAlertButton_jsx",
          elements: {
            Button
          },
          options: {
            use: 'tertiary-light',
            href: '#feedback-form'
          }
        })
      })]
    });
  }
  if (!src) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      marginBlock: "200"
    });
  }
  const preProps = {
    children: {
      props: {
        children: src,
        className: `language-${lang}`
      }
    }
  };
  return /*#__PURE__*/_jsx(Pre, Object.assign({}, preProps));
};
export default GitHubEmbed;